import { styled } from "goober";
import type { KeyboardEvent, MouseEvent as ReactMouseEvent } from "react";
import { queries } from "../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../hooks/useSpaceDisplayName";
import { playClick } from "../lib/sounds";
import { spaceIcon, spaceIconTooltip } from "../lib/spaceIcon";
import { useMakeUrl, routes } from "../lib/urls";
import { Icon } from "./Icon";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

// Defined as <a> for type ergonomics but rendered as <span> by default — the
// badge sits inside list-row anchors and nested <a> would be invalid HTML; the
// asLink prop opts into a real <a> when the badge is standalone.
const Badge = styled("a")`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px 0 6px;
  height: 20px;
  background: var(--alpha4);
  border-radius: 38px;
  color: var(--text-secondary);
  text-decoration: none;
  min-width: 0;
  max-width: 100%;
  cursor: pointer;
  &:hover {
    background: var(--alpha8);
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 1px;
  }
`;

const IconWrapper = styled("span")`
  flex-shrink: 0;
  display: flex;
`;

const Wrapper = styled("span")`
  display: flex;
  min-width: 0;
  max-width: 100%;
`;

interface SpaceBadgeProps {
  space: DisplayableSpace;
  asLink?: boolean;
}

export function SpaceBadge({ space, asLink }: SpaceBadgeProps) {
  const makeUrl = useMakeUrl();
  const displayName = useSpaceDisplayName();
  const [personalSpace] = useQuery(queries.personalSpace());
  const personalSpaceId = personalSpace?.id ?? null;
  const icon = <Icon name={spaceIcon(space.mode)} size={10} />;
  const tooltip = spaceIconTooltip(space.mode);
  const href =
    personalSpaceId && space.id === personalSpaceId
      ? makeUrl(routes.personal)
      : makeUrl(routes.space, { spaceId: space.id });

  const isPlainClick = (
    e: ReactMouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>
  ) => !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey;

  const navigate = () => {
    playClick();
    history.pushState(history.state, "", href);
  };

  const handleMouseDown = (e: ReactMouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    if (e.button === 0 && isPlainClick(e)) navigate();
  };

  const handleClick = (e: ReactMouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (isPlainClick(e)) e.preventDefault();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      navigate();
    }
  };

  return (
    <Wrapper onMouseDown={(e) => e.stopPropagation()}>
      <Badge
        as={asLink ? "a" : "span"}
        href={asLink ? href : undefined}
        role={asLink ? undefined : "link"}
        tabIndex={asLink ? undefined : 0}
        aria-label={displayName(space)}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onKeyDown={asLink ? undefined : handleKeyDown}
      >
        <IconWrapper>
          {tooltip ? <Tooltip content={tooltip}>{icon}</Tooltip> : icon}
        </IconWrapper>
        <Text variant="text-small-md" ellipsis>
          {displayName(space)}
        </Text>
      </Badge>
    </Wrapper>
  );
}
